import { and, eq, gte, inArray, lte } from "drizzle-orm";
import {
  aisle,
  meal_plan_entry,
  pantry_item,
  type PowerSyncDrizzleDB,
  recipe,
  recipe_ingredient,
  recipe_version,
  shopping_item,
  shopping_list,
} from "@mealplan/powersync";
import { liveRecipeFilters } from "../live/filters";
import { parseVersionIngredients } from "../recipes/mutations";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { aggregateIngredients, aggregationKey, type PlannedIngredient } from "./aggregation";

/** `YYYY-MM-DD` local de hoy (para decidir semana pasada vs actual/futura). */
function todayDateKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export interface GenerateShoppingListOptions {
  /** Lunes de la semana (YYYY-MM-DD). */
  weekStart: string;
  /** Domingo de la semana (YYYY-MM-DD). */
  weekEnd: string;
  /** Nombre de la lista si hay que crearla. */
  listName: string;
  /** Restar la despensa del usuario. */
  usePantry?: boolean;
}

export interface GenerateShoppingListResult {
  listId: string;
  /** Ítems auto-generados insertados. */
  generatedCount: number;
  /** Entradas del plan consideradas. */
  entryCount: number;
}

/**
 * Genera (o REGENERA) la lista de compra de una semana a partir del plan.
 *
 * - Suma cantidades de ingredientes iguales (ver aggregation.ts) escaladas por
 *   las porciones planificadas.
 * - Denormaliza el nombre del pasillo del ingrediente en `category`.
 * - Si la lista de esa semana ya existe: conserva los ítems manuales y el
 *   estado `checked` de los ítems equivalentes (misma clave nombre+unidad);
 *   los auto-generados anteriores se reemplazan.
 */
export async function generateShoppingListFromPlan(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: GenerateShoppingListOptions,
): Promise<GenerateShoppingListResult> {
  // 1. Entradas del plan de la semana (con el sello puntero/sello por plato).
  //    `recipe_id` se toma de la RECETA (lado unido), filtrada por la capa live:
  //    una entrada que apunta a una receta en la papelera queda con recipe_id =
  //    null → NO alimenta la lista de compra (ni su nombre ni sus ingredientes).
  //    Antes el join era crudo (`eq(...)` sin filtro) y `recipe_id` venía de
  //    meal_plan_entry, así que una receta borrada seguía filtrando sus
  //    ingredientes a la lista generada (bug de cohorte del invariante disperso).
  const entries = await db
    .select({
      recipe_id: recipe.id,
      servings: meal_plan_entry.servings,
      recipe_servings: recipe.servings,
      recipe_version_id: meal_plan_entry.recipe_version_id,
    })
    .from(meal_plan_entry)
    .leftJoin(recipe, and(eq(meal_plan_entry.recipe_id, recipe.id), ...liveRecipeFilters()))
    .where(
      and(
        eq(meal_plan_entry.user_id, userId),
        gte(meal_plan_entry.plan_date, options.weekStart),
        lte(meal_plan_entry.plan_date, options.weekEnd),
      ),
    );

  const recipeIds = [
    ...new Set(entries.map((e) => e.recipe_id).filter((r): r is string => Boolean(r))),
  ];

  // 2. Ingredientes VIVOS de esas recetas + pasillos del usuario.
  const ingredients =
    recipeIds.length > 0
      ? await db
          .select()
          .from(recipe_ingredient)
          .where(inArray(recipe_ingredient.recipe_id, recipeIds))
      : [];

  const aisles = await db.select().from(aisle).where(eq(aisle.user_id, userId));
  const aisleNameById = new Map(aisles.map((a) => [a.id, a.name]));

  const ingredientsByRecipe = new Map<string, typeof ingredients>();
  for (const ingredient of ingredients) {
    if (!ingredient.recipe_id) {
      continue;
    }
    const bucket = ingredientsByRecipe.get(ingredient.recipe_id) ?? [];
    bucket.push(ingredient);
    ingredientsByRecipe.set(ingredient.recipe_id, bucket);
  }

  // 2b. DECISIÓN DE PRODUCTO (puntero/sello): para SEMANAS PASADAS
  //     (weekEnd < hoy) la lista refleja lo que REALMENTE se planificó → se
  //     prefiere la versión SELLADA de cada plato (`recipe_version.ingredients`
  //     + sus `servings` como recipeServings) cuando esa fila está disponible
  //     localmente (dentro de la ventana de sync). Si el sello cayó fuera de la
  //     ventana (o el plato es legacy sin sello), se degrada al ingrediente
  //     VIVO. Para la semana ACTUAL/FUTURA se usa SIEMPRE la receta viva (la
  //     regeneración refleja las recetas de hoy). Offline-safe: sin la fila
  //     sellada, cae al vivo sin fallar.
  const isPastWeek = options.weekEnd < todayDateKey();
  const sealedIds = isPastWeek
    ? [...new Set(entries.map((e) => e.recipe_version_id).filter((v): v is string => Boolean(v)))]
    : [];
  const sealedVersions =
    sealedIds.length > 0
      ? await db.select().from(recipe_version).where(inArray(recipe_version.id, sealedIds))
      : [];
  const sealedById = new Map(sealedVersions.map((v) => [v.id, v]));

  // 3. Expandir: cada entrada del plan aporta los ingredientes de su receta
  //    (sellada para semanas pasadas, viva en caso contrario) escalados por sus
  //    porciones.
  const planned: PlannedIngredient[] = [];
  for (const entry of entries) {
    if (!entry.recipe_id) {
      continue;
    }
    const sealed =
      isPastWeek && entry.recipe_version_id ? sealedById.get(entry.recipe_version_id) : undefined;
    if (sealed) {
      for (const ing of parseVersionIngredients(sealed.ingredients ?? null)) {
        planned.push({
          name: ing.name,
          quantity: ing.quantity,
          unit: ing.unit,
          category: ing.aisle_id ? (aisleNameById.get(ing.aisle_id) ?? null) : null,
          recipeServings: sealed.servings ?? 1,
          plannedServings: entry.servings ?? null,
        });
      }
      continue;
    }
    const recipeIngredients = ingredientsByRecipe.get(entry.recipe_id) ?? [];
    for (const ingredient of recipeIngredients) {
      planned.push({
        name: ingredient.name ?? "",
        quantity: ingredient.quantity ?? null,
        unit: ingredient.unit ?? null,
        category: ingredient.aisle_id ? (aisleNameById.get(ingredient.aisle_id) ?? null) : null,
        recipeServings: entry.recipe_servings ?? 1,
        plannedServings: entry.servings ?? null,
      });
    }
  }

  // 4. Despensa opcional.
  const pantry = options.usePantry
    ? (await db.select().from(pantry_item).where(eq(pantry_item.user_id, userId))).map((p) => ({
        name: p.name ?? "",
        quantity: p.quantity ?? null,
        unit: p.unit ?? null,
      }))
    : [];

  const aggregated = aggregateIngredients(planned, pantry);

  // 5. Upsert de la lista de la semana + reemplazo de ítems auto-generados.
  const now = nowISO();
  const existingLists = await db
    .select()
    .from(shopping_list)
    .where(and(eq(shopping_list.user_id, userId), eq(shopping_list.week_start, options.weekStart)))
    .limit(1);

  let listId: string;
  const existingList = existingLists[0];

  const checkedByKey = new Map<string, number>();
  if (existingList) {
    listId = existingList.id;
    const previousItems = await db
      .select()
      .from(shopping_item)
      .where(eq(shopping_item.shopping_list_id, listId));
    for (const item of previousItems) {
      if (item.is_manual === 1) {
        continue; // los manuales se conservan tal cual
      }
      if (item.name) {
        checkedByKey.set(aggregationKey(item.name, item.unit), item.checked ?? 0);
      }
    }
  } else {
    listId = generateUUID();
  }

  await db.transaction(async (trx) => {
    if (existingList) {
      await trx
        .update(shopping_list)
        .set({ name: options.listName.trim(), updated_at: now })
        .where(eq(shopping_list.id, listId));
      await trx
        .delete(shopping_item)
        .where(and(eq(shopping_item.shopping_list_id, listId), eq(shopping_item.is_manual, 0)));
    } else {
      await trx.insert(shopping_list).values({
        id: listId,
        user_id: userId,
        name: options.listName.trim(),
        week_start: options.weekStart,
        created_at: now,
        updated_at: now,
      });
    }

    let order = 0;
    for (const item of aggregated) {
      await trx.insert(shopping_item).values({
        id: generateUUID(),
        shopping_list_id: listId,
        user_id: userId,
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        category: item.category,
        checked: checkedByKey.get(item.key) ?? 0,
        is_manual: 0,
        sort_order: order,
        created_at: now,
        updated_at: now,
      });
      order += 1;
    }
  });

  return { listId, generatedCount: aggregated.length, entryCount: entries.length };
}
